import React, { useContext } from 'react'
import cn from 'classnames'
import { CompactCtaContext } from '../../../contexts/compact-cta-context'
import { useCtaButtonMetadata } from '../../../contexts/cta-button-context'
import { useIsSectionThemeDark } from '../../../contexts/section-theme-context'

import { usePlacementContext } from '../../../contexts/placement-context'
import { useBrowserService } from '../../../lib/hooks'
import {
  ComponentCtaButtonSize,
  ComponentCtaButtonType,
  CtaDescriptionPosition,
  CtaDescriptionWidth,
  TypeComponentCtaButton,
} from '../../../lib/types'
import type { ContentProps } from '../../renderer'
import { renderRichTextMultiLineContent, renderRichTextSingleLineContent } from '../../renderer/rich-text'
import { getButtonColor } from './button-color'
import { DefaultInstallButton } from './default'
import { ChromeInstallButton } from './extension/chrome'
import { BrowserInstallButton } from './extension/default'
import { EdgeInstallButton } from './extension/edge'
import { FirefoxInstallButton } from './extension/firefox'
import { SafariInstallButton } from './extension/safari'
import { AndroidWithFallbacksInstallButton } from './keyboard/android'
import { KeyboardInstallButton } from './keyboard/default'
import { IOSInstallButton } from './keyboard/ios'
import { LinkButton } from './link'
import { LlamaDownloadButton } from './llama/default'
import { MacDownloadButton } from './llama/mac'
import { WindowsDownloadButton } from './llama/windows'
import { ComponentLink } from '../link/component-link'
import { Typography } from '@grammarly/badger-ui'
import { SignUpProductRecButton } from './signup-product-recommendation'
import { SignUpGoogleButton } from './signup-google'
import { GoDownloadButton } from './go'

import styles from './cta-button.module.scss'
import { usePageTheme } from '../../../contexts/page-theme-context'

export const CtaButtonContent: React.FC<ContentProps<TypeComponentCtaButton>> = (props) => {
  const {
    className,
    structure: {
      fields: { type, ctaText, showArrow, color, targetLink, size },
      sys: { id },
    },
  } = props

  const { isMobileOrTablet } = useBrowserService()
  const compact = useContext(CompactCtaContext)
  const buttonColor = getButtonColor(color)
  const buttonText = renderRichTextSingleLineContent(ctaText)
  const { placement, placementInternalName } = usePlacementContext()

  const buttonProps = {
    className: cn(className, size === ComponentCtaButtonSize.Large && styles.ctaButtonLarge),
    color: buttonColor,
    text: buttonText,
    showArrow,
    compact,
    placement,
    placementInternalName,
    'data-vqa': id,
  }

  if (type === ComponentCtaButtonType.Link && targetLink) {
    return <LinkButton targetLink={targetLink} {...buttonProps} />
  } else if (type === ComponentCtaButtonType.DefaultProductInstall) {
    return <DefaultInstallButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.IOSInstall) {
    return <IOSInstallButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.AndroidInstall) {
    return <AndroidWithFallbacksInstallButton {...buttonProps} />
  } else if (
    isMobileOrTablet &&
    (type === ComponentCtaButtonType.ExtensionOrKeyboardInstall ||
      type === ComponentCtaButtonType.LlamaOrKeyboardInstall)
  ) {
    return (
      <KeyboardInstallButton color={buttonColor} placement={placement} placementInternalName={placementInternalName} />
    )
  } else if (
    [ComponentCtaButtonType.ExtensionOrKeyboardInstall, ComponentCtaButtonType.ExtensionInstall].includes(type)
  ) {
    return <BrowserInstallButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.ExtensionChromeInstall) {
    return <ChromeInstallButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.ExtensionFirefoxInstall) {
    return <FirefoxInstallButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.ExtensionSafariInstall) {
    return <SafariInstallButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.ExtensionEdgeInstall) {
    return <EdgeInstallButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.LlamaMacInstall) {
    return <MacDownloadButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.LlamaWindowsInstall) {
    return <WindowsDownloadButton {...buttonProps} />
  } else if ([ComponentCtaButtonType.LlamaOrKeyboardInstall, ComponentCtaButtonType.LlamaInstall].includes(type)) {
    return <LlamaDownloadButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.SignUpProductRecommendation) {
    return <SignUpProductRecButton {...buttonProps} />
  } else if (type === ComponentCtaButtonType.SignUpGoogle) {
    return <SignUpGoogleButton size={size} text={buttonText} color={buttonColor} />
  } else if (type === ComponentCtaButtonType.GOInstall && targetLink) {
    return <GoDownloadButton targetLink={targetLink} {...buttonProps} />
  }

  return null
}

export const CtaButtonDescription: React.FC<ContentProps<TypeComponentCtaButton>> = (props) => {
  const {
    structure: {
      fields: { ctaUnderDescription },
    },
  } = props
  const isDarkThemeSection = useIsSectionThemeDark()
  const { color } = usePageTheme()
  const isFixedWidthDescription = useCtaButtonMetadata().descriptionWidth === CtaDescriptionWidth.Fixed
  const ctaDescriptionText = ctaUnderDescription ? renderRichTextMultiLineContent(ctaUnderDescription) : null

  if (!ctaDescriptionText) {
    return null
  }

  return (
    <Typography.TS
      as="p"
      className={cn(styles.ctaDescription, {
        [styles.fixedWidthDescription]: isFixedWidthDescription,
        [color === 'convertTo2024' ? styles.lightDescription24 : styles.lightDescription]: isDarkThemeSection,
      })}
    >
      {ctaDescriptionText}
    </Typography.TS>
  )
}

interface AdditionalCtaButtonProps {
  ctaButtonAndUnderLinkContainerClassName?: string
}

export const CtaButton: React.FC<ContentProps<TypeComponentCtaButton> & AdditionalCtaButtonProps> = (props) => {
  const {
    structure: {
      fields: { ctaUnderLink, internalName },
    },
  } = props
  const { descriptionPosition, showCtaDescription } = useCtaButtonMetadata()
  const isDarkThemeSection = useIsSectionThemeDark()
  const { color } = usePageTheme()
  const ctaDescriptionPositionClass = {
    [CtaDescriptionPosition.Center]: styles.center,
    [CtaDescriptionPosition.Left]: styles.left,
  }[descriptionPosition]

  const { C4 } = Typography

  return (
    <div className={cn(styles.ctaContainer, ctaDescriptionPositionClass)} data-component-id={internalName}>
      {/* ctaButtonContainer className is needed for CtaButtonGroup column and banner styling */}
      <div
        className={cn(
          styles.ctaButtonAndUnderLinkContainer,
          props.ctaButtonAndUnderLinkContainerClassName,
          'ctaButtonContainer'
        )}
      >
        <CtaButtonContent {...props} />
        {ctaUnderLink && (
          <C4
            as="div"
            className={cn(
              styles.ctaUnderLink,
              isDarkThemeSection && styles.lightLink,
              color === 'convertTo2024' ? styles.styles2024 : styles.styles2023
            )}
          >
            <ComponentLink targetLink={ctaUnderLink} />
          </C4>
        )}
      </div>
      {showCtaDescription && <CtaButtonDescription {...props} />}
    </div>
  )
}
